import React, { useEffect } from 'react';
import Collapse from '@mui/material/Collapse';
import { Link, useLocation } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { BsChevronDown, BsChevronUp } from 'react-icons/bs';
import './menu.css';

// ── Logo asset (from public folder) ─────────────────────
const LOGO_SRC = process.env.PUBLIC_URL + '/logo192.png';

// Close sidebar directly — does not depend on any prop chain
const closeSidebarDOM = () => {
    const el = document.getElementById('side-bar');
    if (el) el.classList.remove('show-menu');
};

// ── Menu Item Component ──────────────────────────────────
const MenuItemComponent = ({ menu, onClose }) => {
    const location = useLocation();
    const [open, setOpen] = React.useState(false);

    const isActive = (item) => {
        if (!item || !item.to) return false;
        const currentPath = location.pathname;
        const targetPath = item.to;
        return currentPath === targetPath || (targetPath !== '/' && currentPath.startsWith(targetPath));
    };

    const hasSubmenus = menu.submenus && Array.isArray(menu.submenus) && menu.submenus.length !== 0;
    const active = isActive(menu) || (hasSubmenus && menu.submenus.some(sub => isActive(sub)));

    // Keep accordion open if child path is active
    useEffect(() => {
        if (hasSubmenus && menu.submenus.some(sub => isActive(sub))) {
            setOpen(true);
        }
    }, [location.pathname]);

    const handleToggle = (e) => {
        if (hasSubmenus) {
            e.preventDefault();
            setOpen(prev => !prev);
        }
    };

    // Called on every navigation link click
    const handleLinkClick = () => {
        closeSidebarDOM();     // instant DOM close — no prop chain, always works
        if (onClose) onClose(); // also update Layout state to hide backdrop
    };

    if (hasSubmenus) {
        return (
            <Box sx={{ width: '100%' }}>
                <Box
                    onClick={handleToggle}
                    className={`menu-row ${active ? 'active' : ''}`}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: 'pointer',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span className="menu-icon-wrapper">{menu.icon}</span>
                        <Typography className="menu-row-label" sx={{ fontSize: '13.5px', fontWeight: 500 }} dangerouslySetInnerHTML={{ __html: menu.label }} />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', color: '#64748b' }}>
                        {open ? <BsChevronUp size={12} /> : <BsChevronDown size={12} />}
                    </Box>
                </Box>
                <Collapse in={open} timeout="auto" unmountOnExit>
                    <Box className="submenu-container">
                        {menu.submenus.map((sub, idx) => {
                            const subActive = isActive(sub);
                            return (
                                <Link
                                    to={sub.to}
                                    key={idx}
                                    className={`submenu-row ${subActive ? 'active' : ''}`}
                                    onClick={handleLinkClick}
                                >
                                    <span className="submenu-bullet" />
                                    <Typography
                                        className="submenu-label"
                                        sx={{ fontSize: '13px', fontWeight: subActive ? 600 : 400 }}
                                        dangerouslySetInnerHTML={{ __html: sub.label }}
                                    />
                                </Link>
                            );
                        })}
                    </Box>
                </Collapse>
            </Box>
        );
    }

    return (
        <Link
            to={menu.to}
            className={`menu-row ${active ? 'active' : ''}`}
            onClick={handleLinkClick}
        >
            <span className="menu-icon-wrapper">{menu.icon}</span>
            <Typography
                className="menu-row-label"
                sx={{ fontSize: '13.5px', fontWeight: 500 }}
                dangerouslySetInnerHTML={{ __html: menu.label }}
            />
        </Link>
    );
};

// ── SideBar Component ─────────────────────────────────────
const SideBar = ({ menus, title, mobileOpen, onClose }) => {
    const visibleMenus = menus?.filter(menu => menu && !menu.hide) || [];

    return (
        <div className={`side-bar${mobileOpen ? ' show-menu' : ''}`} id="side-bar">
            {/* Logo area with Title */}
            <div className='sidebar-logo'>
                <img src={LOGO_SRC} alt={title || 'Work Axis'} />
                <span className='sidebar-title'>{title || 'WORK AXIS'}</span>
            </div>

            {/* Menu List */}
            <div className='menu-bar'>
                {visibleMenus.map((menu, index) => (
                    <MenuItemComponent key={index} menu={menu} onClose={onClose} />
                ))}
            </div>
        </div>
    );
};

export default SideBar;